import React from 'react';
import { ReactComponent as Doc } from 'assets/icons/file.svg';
import {
  MedicationPatientInfoOutput,
  MedicationRequestOutputDto,
} from 'graphql/generated/remote-schema-hasura';
import moment from 'moment';
import ButtonComponent from 'components/button/buttonComponent';
import { STANDARD_DATE } from 'utilities/dateHelper';
import { useNavigate } from 'react-router-dom';
import { MY_APPOINTMENTS, MY_VISIT_SUMMARY } from 'utilities/routes';
import { twMerge } from 'tailwind-merge';
import { useAuth } from '../../../../../../../auth/context/AuthContext';
import { Roles } from '../../../../../../../firebase/interfaces';

interface IMedicationHeaderProps extends MedicationRequestOutputDto {
  locale: Record<string, string>;
  isQuestionnaireMedication: boolean;
}

const MedicationHeader: React.FC<IMedicationHeaderProps> = ({
  patientInfo,
  requestedAt,
  providerFullName,
  appointmentCodexId,
  locale,
  isQuestionnaireMedication,
}) => {
  const navigate = useNavigate();
  const dateQuestionnaire = moment(requestedAt).format(
    `[${locale?.prescriptionDate}] ${STANDARD_DATE}`,
  );

  const date = moment(requestedAt).format(
    `[Prescribed date:] ${STANDARD_DATE}`,
  );

  const { user, selectedAccount } = useAuth();

  const isPatientRole = user?.role === Roles.PATIENT;

  const handleSummary = () =>
    navigate(`${MY_APPOINTMENTS}${MY_VISIT_SUMMARY}/${appointmentCodexId}`);

  if (isQuestionnaireMedication) {
    return (
      <div className="flex flex-col desktop:flex-row desktop:items-center items-start px-5 desktop:px-30 border border-transparent border-b-gray-opacity-15 py-4 bg-neutral-100 overflow-hidden desktop:gap-30 gap:2.5">
        <h3 className="justify-start text-neutral-800 text-lg font-semibold leading-normal">
          {locale?.questionnaireMedication ||
            'Self-reported by Patient in Health questionnaire'}
        </h3>
        <p className="justify-start text-med-gray-3 text-base font-medium leading-snug">
          {dateQuestionnaire}
        </p>
      </div>
    );
  }

  return (
    <div className="flex flex-col bg-[#F3EEF9] desktop:flex-row desktop:items-center items-start px-5 desktop:px-30 border border-transparent border-b-gray-opacity-15 py-4 overflow-hidden desktop:gap-30 gap:2.5">
      {isPatientRole && <PatientBadge patientInfo={patientInfo} />}
      <h3 className="justify-start text-neutral-800 text-lg font-semibold leading-normal">
        {locale?.prescriptionFrom}: {providerFullName}{' '}
        {patientInfo.isChild && isPatientRole && !selectedAccount
          ? locale.forYourChild || 'for your child'
          : ''}
      </h3>
      <p className="justify-start text-med-gray-3 text-base font-medium leading-snug">
        {date}
      </p>
      {appointmentCodexId && (
        <ButtonComponent
          testID={`summary-${appointmentCodexId}`}
          Icon={Doc}
          iconPosition="left"
          iconWidth="w-[1.25rem] !stroke-transparent"
          type="underline"
          onClick={handleSummary}
          className="!text-sm desktop:ml-auto mt-5 desktop:mt-0"
        >
          {locale?.summary}
        </ButtonComponent>
      )}
    </div>
  );
};

export default MedicationHeader;

function PatientBadge({
  patientInfo,
}: {
  patientInfo: MedicationPatientInfoOutput;
}) {
  return (
    <div
      className={twMerge(
        'border rounded-5  px-2.5 text-sm',
        !patientInfo.isChild
          ? 'border-btn-black text-btn-black'
          : 'border-alert-positive text-alert-positive',
      )}
    >
      {patientInfo.firstName} {patientInfo.lastName}
    </div>
  );
}
